import { ChatBubbleLeftIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { use, useEffect, useId, useState } from "react";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@fronterahealth/frontera-ui-components/ui/dialog";
import { Field, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";
import { Label } from "@fronterahealth/frontera-ui-components/ui/label";
import { RadioGroup, RadioGroupItem } from "@fronterahealth/frontera-ui-components/ui/radio-group";
import { Textarea } from "@fronterahealth/frontera-ui-components/ui/textarea";

import {
  ReportReviewerStatus,
  ReportSuggestionSourceEnum,
  ReviewItemFieldsFragment,
  useGetReportReviewItemsQuery,
} from "@api/graphql/types-and-hooks";
import { commentFieldTitle, fieldRowLabel } from "@components/QaReview/hooks/useReviewAnchors";
import { ReviewDataContext } from "@components/QaReview/hooks/useReviewData";
import { useFinalApproverWaitReason } from "@components/ReportReview/hooks/useFinalApproverWaitReason";
import { Decision, useFinishReview } from "@components/ReportReview/hooks/useFinishReview";
import { useMyAuth0Id } from "@components/ReportReview/hooks/useMyAuth0Id";
import { useAuthorName } from "@components/ReportReview/hooks/useRequesterName";
import { useReviewStatus } from "@components/ReportReview/hooks/useReviewStatus";
import { reviewReportName } from "@components/ReportReview/reviewReportName";
import { useReport } from "@providers/ReportProvider";

const isMyOpenComment = (item: ReviewItemFieldsFragment | null | undefined, myId: string | undefined) =>
  item?.__typename === "ReportCommentType" &&
  !item.parent &&
  item.author?.id === myId &&
  !item.isDeleted &&
  !item.hidden &&
  !isReviewItemResolved(item);

/** How long the opening choice waits for a refetch in flight before going with the count it has. */
export const COUNT_WAIT_MS = 3000;

/** The reviewer's own open root comments (replies excluded) and how far their count has got. */
const useMyComments = (enabled: boolean) => {
  const myId = useMyAuth0Id();
  const { report } = useReport();
  const provider = use(ReviewDataContext);
  // The provider holds no items when the review scan is off, so fall back to reading the reviewer's comments here.
  const useProvider = !!provider?.items?.length;
  const query = useGetReportReviewItemsQuery(
    { reportId: report.id, filter: { source: ReportSuggestionSourceEnum.PeerReview } },
    { enabled: enabled && !useProvider },
  );
  const source = useProvider ? provider.openComments : query.data?.getReportReviewItems ?? [];
  // Failed only once the retries are over with no count ever loaded; a failed refetch keeps the last count.
  const countFailed = !useProvider && !query.data && query.isError && !query.isFetching;
  const isLoading = !useProvider && !query.data && !countFailed;
  // Nothing in flight: a comment just left invalidates the items without waiting for them.
  const isSettled = useProvider ? !provider.isFetching : !query.isFetching;
  const comments = source.filter((item): item is ReviewItemFieldsFragment => isMyOpenComment(item, myId));
  return { comments, isLoading, isSettled, countFailed };
};

/**
 * With no choice made, picks Send back when the reviewer left comments, or the count failed, and Approve when they
 * left none. It waits for a refetch in flight, but no longer than COUNT_WAIT_MS, so a slow poll never leaves the
 * dialog without a choice. The pick is the provider's decision, so a refetch never switches it and each opening
 * picks afresh.
 */
const useOpeningDecision = (open: boolean, { comments, isLoading, isSettled, countFailed }: MyComments) => {
  const { decision, setDecision } = useFinishReview();
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setWaitedOut(true), COUNT_WAIT_MS);
    return () => {
      clearTimeout(timer);
      setWaitedOut(false);
    };
  }, [open]);

  const ready = open && !decision && !isLoading && (isSettled || waitedOut);
  const hasComments = countFailed || comments.length > 0;
  useEffect(() => {
    if (ready) setDecision(hasComments ? ReportReviewerStatus.ChangesRequested : ReportReviewerStatus.Approved);
  }, [ready, hasComments, setDecision]);
};

type MyComments = ReturnType<typeof useMyComments>;

// Until a choice exists the disabled button says what it is waiting for.
const submitLabel = (decision: Decision | undefined, authorName: string | undefined) => {
  if (!decision) return "Checking your comments…";
  return decision === ReportReviewerStatus.ChangesRequested ? `Send back to ${authorName ?? "the author"}` : "Approve";
};

export const FinishReviewDialog: React.FC = () => {
  const noteId = useId();
  const { open, setOpen, decision, setDecision, note, setNote, discard } = useFinishReview();
  const { setStatus, isPending } = useReviewStatus();
  const { report } = useReport();
  const authorName = useAuthorName("") || undefined;
  const myComments = useMyComments(open);
  const { comments } = myComments;
  useOpeningDecision(open, myComments);
  // A final approver may have to wait for the other reviewers; sending back is always allowed.
  const waitReason = useFinalApproverWaitReason();

  // A blocked final approver can only send back, so show that choice instead of a selected but disabled Approve.
  const effectiveDecision = waitReason ? ReportReviewerStatus.ChangesRequested : decision;
  const sendingBack = effectiveDecision === ReportReviewerStatus.ChangesRequested;
  // An unknown count (loading or failed) shows no comments, so it can't stand in for the note either.
  const noteRequired = sendingBack && !comments.length;
  const trimmed = note.trim();
  const canSubmit = !!effectiveDecision && !isPending && (!noteRequired || !!trimmed);

  // Escape, outside click and the X only hide the dialog; the draft stays until Cancel or a successful send.
  const onOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    setOpen(next);
  };

  // useReviewStatus toasts a failure; the dialog stays open with the draft so the reviewer can retry.
  const submit = async () => {
    if (!effectiveDecision) return;
    try {
      const result = await setStatus(effectiveDecision, { note: trimmed || undefined });
      if (result?.setReportReviewStatus?.success) discard();
    } catch {
      // Already reported by the toast.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* With no author name there is no description, and Radix needs aria-describedby cleared to say so. */}
      <DialogContent className="sm:max-w-lg" {...(!authorName && { "aria-describedby": undefined })}>
        <DialogHeader>
          <DialogTitle>Finish review of the {reviewReportName(report)}</DialogTitle>
          {authorName && <DialogDescription>Authored by {authorName}</DialogDescription>}
        </DialogHeader>

        <RadioGroup
          value={effectiveDecision ?? ""}
          onValueChange={(next) => setDecision(next as Decision)}
          className="flex gap-8 rounded-lg border border-input px-4 py-2"
        >
          <div className="flex items-center gap-3">
            <RadioGroupItem
              id={`${noteId}-approve`}
              value={ReportReviewerStatus.Approved}
              disabled={!!waitReason}
              aria-describedby={waitReason ? `${noteId}-wait` : undefined}
            />
            <Label htmlFor={`${noteId}-approve`}>Approve</Label>
          </div>
          <div className="flex items-center gap-3">
            <RadioGroupItem id={`${noteId}-send-back`} value={ReportReviewerStatus.ChangesRequested} />
            <Label htmlFor={`${noteId}-send-back`}>Send back for changes</Label>
          </div>
        </RadioGroup>
        {waitReason && (
          <p id={`${noteId}-wait`} className="text-xs text-text-tertiary">
            {waitReason}
          </p>
        )}

        <Field>
          <FieldLabel htmlFor={noteId}>{noteRequired ? "Note" : "Note (optional)"}</FieldLabel>
          <Textarea id={noteId} value={note} onChange={(event) => setNote(event.target.value)} rows={3} />
        </Field>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold">Sent with your note · {comments.length}</span>
            <button type="button" className="text-blurple-600 hover:underline" onClick={() => setOpen(false)}>
              + Comment on another field
            </button>
          </div>
          {comments.length ? (
            <ul className="space-y-2">
              {comments.map((item) => {
                const isEdit = item.__typename === "ReportCommentType" && item.proposedValue != null;
                const Icon = isEdit ? PencilSquareIcon : ChatBubbleLeftIcon;
                return (
                  <li key={item.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                    <Icon className="size-4 shrink-0 text-text-tertiary" aria-hidden />
                    <span className="font-semibold">{fieldRowLabel(commentFieldTitle(item))}</span>
                    <span className="min-w-0 truncate text-text-secondary">{item.content}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-text-tertiary">No field comments yet</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={discard}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => void submit()}>
            {submitLabel(effectiveDecision, authorName)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
